/**
 * Phase 43 (2026-07-28) - ThemeProvider + useAppearance hook.
 *
 * Sets `data-theme`, `data-accent`, `data-font-size`, `data-contrast` on
 * <html> based on user preferences. Reads localStorage synchronously on
 * boot (avoiding a flash of default theme), fetches the authoritative copy
 * from /api/user/appearance after login, and PUTs updates back with a 400ms
 * debounce.
 *
 * When `mode === "system"`, listens to `prefers-color-scheme` and toggles
 * between light and dark automatically.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import api from "@/lib/api";

const STORAGE_KEY = "somturk-theme-v1";

const DEFAULTS = {
  theme_mode: "system",   // system | light | dark | eye_comfort | high_contrast
  accent_color: "blue",    // blue | navy | green | grey | purple
  font_size: "md",         // sm | md | lg | xl
  contrast: "standard",    // standard | high
};

/** Map backend theme_mode -> data-theme attribute value applied on <html>. */
function resolveDataTheme(mode) {
  if (mode === "light") return "light";
  if (mode === "dark") return "dark";
  if (mode === "eye_comfort") return "eye-comfort";
  if (mode === "high_contrast") return "high-contrast";
  // "system" — mirror the OS preference
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "light";
}

function applyToHtml(state) {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  el.setAttribute("data-theme", resolveDataTheme(state.theme_mode));
  el.setAttribute("data-accent", state.accent_color);
  el.setAttribute("data-font-size", state.font_size);
  el.setAttribute("data-contrast", state.contrast);
}

function loadCached() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULTS, ...parsed };
  } catch { return { ...DEFAULTS }; }
}

/** Pre-boot apply — call this ONCE at module load, before React renders. */
export function initTheme() {
  const cached = loadCached();
  applyToHtml(cached);
}
initTheme();

const AppearanceContext = createContext(null);

export function ThemeProvider({ children }) {
  const [state, setState] = useState(() => loadCached());
  const debounceRef = useRef(null);
  const bootstrappedFromApi = useRef(false);

  // Apply on mount + on every state change.
  useEffect(() => { applyToHtml(state); }, [state]);

  // If mode === 'system', re-apply when the OS preference flips.
  useEffect(() => {
    if (state.theme_mode !== "system") return;
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyToHtml(state);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [state]);

  // Fetch authoritative copy from the backend on first mount if a token exists.
  useEffect(() => {
    const token = localStorage.getItem("st_token");
    if (!token || bootstrappedFromApi.current) return;
    bootstrappedFromApi.current = true;
    (async () => {
      try {
        const { data } = await api.get("/user/appearance");
        setState((prev) => {
          const next = { ...prev, ...data };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
          return next;
        });
      } catch { /* not logged in yet, or backend down - keep cached */ }
    })();
  }, []);

  const pushToBackend = useCallback((next) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try { await api.put("/user/appearance", next); }
      catch { /* offline safe; localStorage is source of truth until reconnect */ }
    }, 400);
  }, []);

  const update = useCallback((patch) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      pushToBackend(patch);
      return next;
    });
  }, [pushToBackend]);

  const value = useMemo(() => ({
    mode: state.theme_mode,
    accent: state.accent_color,
    fontSize: state.font_size,
    contrast: state.contrast,
    setMode: (m) => update({ theme_mode: m }),
    setAccent: (a) => update({ accent_color: a }),
    setFontSize: (s) => update({ font_size: s }),
    setContrast: (c) => update({ contrast: c }),
    reset: () => update({ ...DEFAULTS }),
    reloadFromServer: async () => {
      try {
        const { data } = await api.get("/user/appearance");
        setState(data);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch { /* no-op */ }
    },
  }), [state, update]);

  return (
    <AppearanceContext.Provider value={value}>
      {children}
    </AppearanceContext.Provider>
  );
}

export function useAppearance() {
  const ctx = useContext(AppearanceContext);
  if (!ctx) {
    // Component used outside provider — fall back to a read-only view of the
    // current DOM attributes so it never crashes.
    const el = typeof document !== "undefined" ? document.documentElement : null;
    return {
      mode: DEFAULTS.theme_mode,
      accent: el?.getAttribute("data-accent") || DEFAULTS.accent_color,
      fontSize: el?.getAttribute("data-font-size") || DEFAULTS.font_size,
      contrast: el?.getAttribute("data-contrast") || DEFAULTS.contrast,
      setMode: () => {}, setAccent: () => {}, setFontSize: () => {},
      setContrast: () => {}, reset: () => {}, reloadFromServer: async () => {},
    };
  }
  return ctx;
}
